import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { API_URL } from "../config/api";
import {
  Mail,
  RefreshCw,
  Search,
  Calendar,
  Ship,
  Package,
  MapPin,
  Users,
  Download,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Database,
  BarChart3,
  Eye,
  Upload,
  Square,
  Play,
  Clock,
  Edit,
  Save,
  X,
  AlertTriangle,
  DollarSign,
  FileText,
  Building,
  User,
  Globe,
  Filter,
  Maximize2,
} from "lucide-react";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";


export default function OfferArchivePage() {
  const { user, token } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("archive");
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(null);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [days, setDays] = useState(30);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const limit = 50;
  
  // Email repository state
  const [repoEmails, setRepoEmails] = useState([]);
  const [repoTotal, setRepoTotal] = useState(0);
  const [repoPage, setRepoPage] = useState(0);
  const [repoSearch, setRepoSearch] = useState("");
  
  // Selected email for detail view
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedFields, setEditedFields] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [showFullBody, setShowFullBody] = useState(false);
  
  // Advanced search filters
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const fetchEmails = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: page * limit,
        limit: limit.toString(),
        days: days.toString(),
      });
      
      if (search) params.append("search", search);
      if (serviceFilter && serviceFilter !== "all") params.append("service_type", serviceFilter);
      
      const response = await axios.get(
        `${API_URL}/offer-archive/emails?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setEmails(response.data.emails);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching emails:", error);
      toast.error("Failed to fetch offer archive");
    } finally {
      setLoading(false);
    }
  }, [token, page, search, serviceFilter, days]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/offer-archive/stats`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, [token]);

  const fetchEmailRepository = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        skip: (repoPage * 100).toString(),
        limit: "100",
      });
      
      if (repoSearch) params.append("search", repoSearch);
      
      const response = await axios.get(
        `${API_URL}/offer-archive/email-repository?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setRepoEmails(response.data.emails);
      setRepoTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching email repository:", error);
    }
  }, [token, repoPage, repoSearch]);

  useEffect(() => {
    fetchEmails();
    fetchStats();
  }, [fetchEmails, fetchStats]);

  useEffect(() => {
    if (activeTab === "repository") {
      fetchEmailRepository();
    }
  }, [activeTab, fetchEmailRepository]);

  // Check for existing sync job on load and poll for progress
  useEffect(() => {
    const checkExistingSync = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/offer-archive/sync/progress`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.status === "running" || response.data.status === "in_progress") {
          setSyncing(true);
          setSyncProgress(response.data);
        }
      } catch (error) {
        // No sync job running
      }
    };
    if (token) {
      checkExistingSync();
    }
  }, [token]);

  // Poll for sync progress when syncing
  useEffect(() => {
    let interval;
    if (syncing) {
      interval = setInterval(async () => {
        try {
          const response = await axios.get(
            `${API_URL}/offer-archive/sync/progress`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setSyncProgress(response.data);
          
          // Check if completed or stopped
          if (response.data.status === "completed" || response.data.status === "stopped" || response.data.status === "failed" || response.data.status === "cancelled") {
            setSyncing(false);
            setSyncProgress(null);
            fetchEmails();
            fetchStats();
            
            if (response.data.status === "completed") {
              toast.success(`Sync complete! ${response.data.progress?.new_count || 0} new emails imported.`);
            } else if (response.data.status === "stopped" || response.data.status === "cancelled") {
              toast.info("Sync was stopped.");
            } else {
              toast.error("Sync failed: " + (response.data.progress?.error || "Unknown error"));
            }
          }
        } catch (error) {
          console.error("Error fetching sync progress:", error);
        }
      }, 3000); // Poll every 3 seconds
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [syncing, token, fetchEmails, fetchStats]);

  const handleSync = async (maxEmails = 500) => {
    setSyncing(true);
    setSyncProgress(null);
    try {
      const response = await axios.post(
        `${API_URL}/offer-archive/sync?max_emails=${maxEmails}&batch_size=300&skip_existing=true`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.status === "already_running") {
        toast.info("A sync job is already running. Monitoring progress...");
      } else {
        toast.success(`Sync started! Processing up to ${maxEmails.toLocaleString()} emails...`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start sync");
      setSyncing(false);
    }
  };

  const handleSyncAll = async () => {
    if (!window.confirm("This will sync ALL emails from offer@europacific.com (14,000+ emails). This may take 30-60 minutes. Continue?")) {
      return;
    }
    handleSync(50000);
  };

  const handleStopSync = async () => {
    try {
      await axios.post(
        `${API_URL}/offer-archive/sync/stop`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.info("Stopping sync...");
    } catch (error) {
      toast.error("Failed to stop sync");
    }
  };

  // Batch upload configuration
  const BATCH_SIZE = 50;
  const BATCH_DELAY_MS = 300000; // 5 minutes between batches
  const [uploadProgress, setUploadProgress] = useState(null);
  const [uploadCancelled, setUploadCancelled] = useState(false);

  const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    const totalFiles = files.length;
    const totalBatches = Math.ceil(totalFiles / BATCH_SIZE);
    
    setUploadCancelled(false);
    setUploadProgress({
      totalFiles,
      totalBatches,
      currentBatch: 0,
      processedFiles: 0,
      successCount: 0,
      errorCount: 0,
      status: 'starting'
    });
    
    let successCount = 0;
    let errorCount = 0;
    
    for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
      // Check if cancelled
      if (uploadCancelled) {
        toast.info("Upload cancelled by user");
        break;
      }
      
      const start = batchIndex * BATCH_SIZE;
      const end = Math.min(start + BATCH_SIZE, totalFiles);
      const batch = files.slice(start, end);
      
      setUploadProgress(prev => ({
        ...prev,
        currentBatch: batchIndex + 1,
        status: `Processing batch ${batchIndex + 1}/${totalBatches}`
      }));
      
      // Process batch
      for (const file of batch) {
        if (uploadCancelled) break;
        
        try {
          const formData = new FormData();
          formData.append("file", file);
          
          await axios.post(
            `${API_URL}/offer-archive/upload`,
            formData,
            { 
              headers: { 
                Authorization: `Bearer ${token}`,
                "Content-Type": "multipart/form-data"
              } 
            }
          );
          successCount++;
        } catch (error) {
          console.error(`Error uploading ${file.name}:`, error);
          errorCount++;
        }
        
        setUploadProgress(prev => ({
          ...prev,
          processedFiles: prev.processedFiles + 1,
          successCount,
          errorCount
        }));
      }
      
      // Refresh data after each batch
      fetchEmails();
      fetchStats();
      
      // Wait between batches (except after last batch)
      if (batchIndex < totalBatches - 1 && !uploadCancelled) {
        setUploadProgress(prev => ({
          ...prev,
          status: `Batch ${batchIndex + 1} complete. Waiting 5 minutes before next batch...`,
          waitingUntil: Date.now() + BATCH_DELAY_MS
        }));
        
        // Wait with countdown
        const startWait = Date.now();
        while (Date.now() - startWait < BATCH_DELAY_MS) {
          if (uploadCancelled) break;
          const remaining = Math.ceil((BATCH_DELAY_MS - (Date.now() - startWait)) / 1000);
          setUploadProgress(prev => ({
            ...prev,
            waitingSeconds: remaining
          }));
          await sleep(1000);
        }
      }
    }
    
    setUploadProgress(null);
    
    if (successCount > 0) {
      toast.success(`Uploaded ${successCount} file(s) successfully`);
    }
    if (errorCount > 0) {
      toast.error(`Failed to upload ${errorCount} file(s)`);
    }
  };

  const cancelUpload = () => {
    setUploadCancelled(true);
  };

  const handleExportRepository = async (format) => {
    try {
      const response = await axios.post(
        `${API_URL}/offer-archive/email-repository/export?format=${format}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (format === "csv") {
        const blob = new Blob([response.data.csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `email_repository_${new Date().toISOString().split("T")[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(`Exported ${response.data.count} emails`);
      } else {
        const blob = new Blob([JSON.stringify(response.data.emails, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `email_repository_${new Date().toISOString().split("T")[0]}.json`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(`Exported ${response.data.count} emails`);
      }
    } catch (error) {
      toast.error("Failed to export");
    }
  };

  // Handle editing fields
  const handleStartEdit = () => {
    setEditedFields({
      customer_name: selectedEmail.customer_name || "",
      pol: selectedEmail.pol || "",
      pod: selectedEmail.pod || "",
      final_destination: selectedEmail.final_destination || "",
      service_type: selectedEmail.service_type || "",
      equipment: selectedEmail.equipment || "",
      incoterm: selectedEmail.incoterm || "",
      shipping_line: selectedEmail.shipping_line || "",
      cargo_description: selectedEmail.cargo_description || "",
      hs_codes: selectedEmail.hs_codes || "",
      weight: selectedEmail.weight || "",
      volume: selectedEmail.volume || "",
      pricing_trucking: selectedEmail.pricing_trucking || "",
      pricing_customs: selectedEmail.pricing_customs || "",
      pricing_forwarding: selectedEmail.pricing_forwarding || "",
      contact_person: selectedEmail.contact_person || "",
      contact_email: selectedEmail.contact_email || selectedEmail.sender_email || "",
    });
    setIsEditing(true);
  };

  const handleSaveEdit = async () => {
    setSavingEdit(true);
    try {
      await axios.put(
        `${API_URL}/offer-archive/emails/${selectedEmail.email_id}`,
        editedFields,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setSelectedEmail({ ...selectedEmail, ...editedFields });
      setIsEditing(false);
      toast.success("Changes saved successfully");
      
      // Refresh list
      fetchEmails();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save changes");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedFields({});
  };

  const clearFilters = () => {
    setSearch("");
    setServiceFilter("all");
    setDateFrom("");
    setDateTo("");
    setDays(365);
    setPage(0);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="container mx-auto px-4 py-6 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                <Mail className="h-6 w-6 text-primary" />
                Email Offer Archive
              </h1>
              <p className="text-sm text-muted-foreground">
                Import and search your legacy email offers
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              {syncing ? (
                <Button
                  onClick={handleStopSync}
                  variant="destructive"
                  data-testid="stop-sync-btn"
                >
                  <Square className="h-4 w-4 mr-2" />
                  Stop Sync
                </Button>
              ) : (
                <>
                  <Button
                    onClick={() => handleSync(500)}
                    variant="outline"
                    data-testid="sync-offers-btn"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </Button>
                  <Button
                    onClick={handleSyncAll}
                    className="bg-primary hover:bg-primary/90"
                    data-testid="sync-all-offers-btn"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload .msg Files
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Sync Progress */}
          {syncing && syncProgress?.progress && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-primary/10 border border-primary/20 rounded-lg p-4"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  <span className="font-medium text-primary">Syncing emails from offer@europacific.com</span>
                </div>
                <span className="text-sm text-primary font-medium">
                  {syncProgress.progress.percent_complete}%
                </span>
              </div>
              <Progress value={syncProgress.progress.percent_complete} className="h-2 mb-2" />
              <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                <span>Total in inbox: <strong>{syncProgress.progress.total_in_inbox?.toLocaleString()}</strong></span>
                <span>Processing: <strong>{syncProgress.progress.processed?.toLocaleString()}</strong> / {syncProgress.progress.total_to_process?.toLocaleString()}</span>
                <span className="text-green-600">New: <strong>{syncProgress.progress.new_count?.toLocaleString()}</strong></span>
                <span className="text-blue-600">Skipped (existing): <strong>{syncProgress.progress.skipped_count?.toLocaleString()}</strong></span>
                <span>Batch: {syncProgress.progress.current_batch} / {syncProgress.progress.total_batches}</span>
              </div>
            </motion.div>
          )}

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-card p-4 rounded-lg border">
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <BarChart3 className="h-4 w-4" />
                  Total Emails
                </div>
                <p className="text-2xl font-bold text-foreground">{stats.total_emails?.toLocaleString()}</p>
              </div>
              <div className="bg-card p-4 rounded-lg border">
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Package className="h-4 w-4" />
                  FCL Offers
                </div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.service_types?.find(s => s.type === "FCL")?.count || 0}
                </p>
              </div>
              <div className="bg-card p-4 rounded-lg border">
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Ship className="h-4 w-4" />
                  LCL Offers
                </div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.service_types?.find(s => s.type === "LCL")?.count || 0}
                </p>
              </div>
              <div className="bg-card p-4 rounded-lg border">
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Mail className="h-4 w-4" />
                  Air Offers
                </div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.service_types?.find(s => s.type === "Air")?.count || 0}
                </p>
              </div>
              <div className="bg-card p-4 rounded-lg border">
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Users className="h-4 w-4" />
                  Unique Customers
                </div>
                <p className="text-2xl font-bold text-foreground">{stats.email_repository_count}</p>
              </div>
            </div>
          )}

          {/* Batch Upload Progress */}
          {uploadProgress && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                  <span className="font-medium text-blue-900">Batch Upload in Progress</span>
                </div>
                <Button variant="destructive" size="sm" onClick={cancelUpload}>
                  Cancel
                </Button>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-blue-700">
                  <span>Batch {uploadProgress.currentBatch} of {uploadProgress.totalBatches}</span>
                  <span>{uploadProgress.processedFiles} / {uploadProgress.totalFiles} files</span>
                </div>
                
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all"
                    style={{ width: `${(uploadProgress.processedFiles / uploadProgress.totalFiles) * 100}%` }}
                  />
                </div>
                
                <div className="flex justify-between text-xs text-blue-600">
                  <span>✓ {uploadProgress.successCount} success</span>
                  {uploadProgress.errorCount > 0 && (
                    <span className="text-red-600">✗ {uploadProgress.errorCount} errors</span>
                  )}
                </div>
                
                {uploadProgress.waitingSeconds && (
                  <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-amber-700 text-center">
                    <Clock className="h-4 w-4 inline mr-1" />
                    Waiting {Math.floor(uploadProgress.waitingSeconds / 60)}:{String(uploadProgress.waitingSeconds % 60).padStart(2, '0')} before next batch...
                    <p className="text-xs mt-1">This prevents system overload</p>
                  </div>
                )}
                
                <p className="text-xs text-blue-500 mt-2">
                  Processing {BATCH_SIZE} emails per batch with 5-minute delays to prevent crashes
                </p>
              </div>
            </div>
          )}

          {/* Drag & Drop Upload Area */}
          <div 
            className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center hover:border-primary/50 transition-colors cursor-pointer"
            onClick={() => document.getElementById("msg-file-upload").click()}
            onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("border-primary"); }}
            onDragLeave={(e) => { e.currentTarget.classList.remove("border-primary"); }}
            onDrop={async (e) => {
              e.preventDefault();
              e.currentTarget.classList.remove("border-primary");
              const files = Array.from(e.dataTransfer.files).filter(f => f.name.endsWith(".msg") || f.name.endsWith(".eml"));
              if (files.length > 0) {
                await handleFileUpload(files);
              } else {
                toast.error("Please drop .msg or .eml files only");
              }
            }}
          >
            <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-muted-foreground">Drag & drop .msg files here</p>
            <p className="text-xs text-muted-foreground mt-1">or click "Upload .msg Files" button above</p>
            <p className="text-xs text-blue-600 mt-2">Files processed in batches of 50 with 5-min delays</p>
            <input
              id="msg-file-upload"
              type="file"
              accept=".msg,.eml"
              multiple
              className="hidden"
              onChange={(e) => handleFileUpload(Array.from(e.target.files))}
            />
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger value="archive" className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Offer Emails
              </TabsTrigger>
              <TabsTrigger value="repository" className="flex items-center gap-2">
                <Database className="h-4 w-4" />
                Email Repository
              </TabsTrigger>
            </TabsList>

            {/* Archive Tab */}
            <TabsContent value="archive" className="space-y-4">
              {/* Enhanced Search & Filters */}
              <div className="bg-card border rounded-lg p-4 space-y-4">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Filter className="h-4 w-4" />
                  Search & Filters
                </div>
                
                {/* Main Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by customer, route, subject, cargo..."
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(0);
                    }}
                    className="pl-10 h-11 text-base"
                    data-testid="search-offers-input"
                  />
                </div>
                
                {/* Filter Row */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <Select value={serviceFilter} onValueChange={(v) => { setServiceFilter(v); setPage(0); }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Service Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="FCL">FCL</SelectItem>
                      <SelectItem value="LCL">LCL</SelectItem>
                      <SelectItem value="Air">Air</SelectItem>
                      <SelectItem value="Breakbulk/RORO">Breakbulk/RORO</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <div>
                    <Input
                      type="date"
                      placeholder="From date"
                      value={dateFrom}
                      onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
                      className="w-full"
                    />
                  </div>
                  
                  <div>
                    <Input
                      type="date"
                      placeholder="To date"
                      value={dateTo}
                      onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
                      className="w-full"
                    />
                  </div>
                  
                  <Select value={days.toString()} onValueChange={(v) => { setDays(parseInt(v)); setPage(0); }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Time Range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">Last 7 Days</SelectItem>
                      <SelectItem value="30">Last 30 Days</SelectItem>
                      <SelectItem value="90">Last 90 Days</SelectItem>
                      <SelectItem value="180">Last 6 Months</SelectItem>
                      <SelectItem value="365">Last Year</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Button 
                    variant="outline" 
                    onClick={clearFilters}
                    className="flex items-center gap-2"
                  >
                    <X className="h-4 w-4" />
                    Clear
                  </Button>
                </div>
                
                {/* Active Filters Display */}
                {(search || serviceFilter !== "all" || dateFrom || dateTo) && (
                  <div className="flex flex-wrap gap-2 pt-2 border-t">
                    <span className="text-xs text-muted-foreground">Active filters:</span>
                    {search && (
                      <Badge variant="secondary" className="text-xs">
                        Search: "{search}"
                      </Badge>
                    )}
                    {serviceFilter !== "all" && (
                      <Badge variant="secondary" className="text-xs">
                        Type: {serviceFilter}
                      </Badge>
                    )}
                    {dateFrom && (
                      <Badge variant="secondary" className="text-xs">
                        From: {dateFrom}
                      </Badge>
                    )}
                    {dateTo && (
                      <Badge variant="secondary" className="text-xs">
                        To: {dateTo}
                      </Badge>
                    )}
                  </div>
                )}
              </div>

              {/* Results count */}
              <div className="flex items-center justify-between text-sm">
                <p className="text-muted-foreground">
                  <span className="font-semibold text-foreground">{total.toLocaleString()}</span> archived offers found
                </p>
              </div>

              {/* Emails Table */}
              <div className="bg-card rounded-lg border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Sender</TableHead>
                      <TableHead>Subject</TableHead>
                      <TableHead>Route</TableHead>
                      <TableHead>Service</TableHead>
                      <TableHead>Equipment</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" />
                        </TableCell>
                      </TableRow>
                    ) : emails.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          No emails found. Click "Sync Inbox" to fetch from offer@europacific.com
                        </TableCell>
                      </TableRow>
                    ) : (
                      emails.map((email) => (
                        <TableRow key={email.email_id} className="cursor-pointer hover:bg-muted/50">
                          <TableCell className="text-sm">
                            {email.received_date ? new Date(email.received_date).toLocaleDateString() : "-"}
                          </TableCell>
                          <TableCell>
                            <div className="max-w-[150px]">
                              <p className="font-medium text-sm truncate">{email.customer_name || email.sender_name || "-"}</p>
                              <p className="text-xs text-muted-foreground truncate">{email.sender_email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm max-w-[200px] truncate" title={email.subject}>
                              {email.subject || "-"}
                            </p>
                          </TableCell>
                          <TableCell>
                            {(email.pol || email.pod) ? (
                              <div className="flex items-center gap-1 text-sm">
                                <MapPin className="h-3 w-3 text-primary" />
                                <span>{email.pol || "?"}</span>
                                <span>→</span>
                                <span>{email.pod || "?"}</span>
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-sm">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {email.service_type ? (
                              <Badge variant="outline">{email.service_type}</Badge>
                            ) : (
                              <span className="text-muted-foreground text-sm">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {email.equipment ? (
                              <Badge variant="secondary">{email.equipment}</Badge>
                            ) : (
                              <span className="text-muted-foreground text-sm">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedEmail(email)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm">
                    Page {page + 1} of {totalPages || 1}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => p + 1)}
                    disabled={page >= totalPages - 1}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* Email Repository Tab */}
            <TabsContent value="repository" className="space-y-4">
              <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
                <div className="flex-1 relative max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search emails..."
                    value={repoSearch}
                    onChange={(e) => {
                      setRepoSearch(e.target.value);
                      setRepoPage(0);
                    }}
                    className="pl-10"
                  />
                </div>
                
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => handleExportRepository("csv")}>
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                  </Button>
                  <Button variant="outline" onClick={() => handleExportRepository("json")}>
                    <Download className="h-4 w-4 mr-2" />
                    Export JSON
                  </Button>
                </div>
              </div>

              <div className="bg-card rounded-lg border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Sources</TableHead>
                      <TableHead>Occurrences</TableHead>
                      <TableHead>First Seen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {repoEmails.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          No emails in repository yet. Sync the offer inbox to populate.
                        </TableCell>
                      </TableRow>
                    ) : (
                      repoEmails.map((email, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{email.email}</TableCell>
                          <TableCell>{email.name || "-"}</TableCell>
                          <TableCell>{email.company || "-"}</TableCell>
                          <TableCell>
                            <div className="flex gap-1 flex-wrap">
                              {email.sources?.map((s, i) => (
                                <Badge key={i} variant="outline" className="text-xs">
                                  {s}
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell>{email.occurrence_count}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {email.first_seen ? new Date(email.first_seen).toLocaleDateString() : "-"}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Total: {repoTotal} emails in repository
                </p>
              </div>
            </TabsContent>
          </Tabs>
        </motion.div>
      </main>

      {/* Email Detail Dialog - Enhanced with Editable Fields */}
      <Dialog open={!!selectedEmail} onOpenChange={() => { setSelectedEmail(null); setIsEditing(false); setShowFullBody(false); }}>
        <DialogContent className={`${showFullBody ? 'max-w-5xl' : 'max-w-3xl'} max-h-[90vh] overflow-y-auto`}>
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-primary" />
                Offer Details
              </DialogTitle>
              <div className="flex items-center gap-2">
                {!isEditing ? (
                  <Button variant="outline" size="sm" onClick={handleStartEdit} data-testid="edit-offer-btn">
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                ) : (
                  <>
                    <Button variant="outline" size="sm" onClick={handleCancelEdit}>
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                    <Button size="sm" onClick={handleSaveEdit} disabled={savingEdit} className="bg-green-600 hover:bg-green-700">
                      {savingEdit ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                      Save
                    </Button>
                  </>
                )}
              </div>
            </div>
          </DialogHeader>
          
          {selectedEmail && (
            <div className="space-y-6">
              {/* Header Info */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-lg mb-2">{selectedEmail.subject}</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">From:</span>{" "}
                    <span className="font-medium">{selectedEmail.sender_name || selectedEmail.sender_email}</span>
                    <span className="text-muted-foreground ml-2">&lt;{selectedEmail.sender_email}&gt;</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Date:</span>{" "}
                    <span className="font-medium">
                      {selectedEmail.received_date ? new Date(selectedEmail.received_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : "-"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Customer Details Section */}
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold text-sm flex items-center gap-2 mb-4 text-blue-700">
                  <User className="h-4 w-4" />
                  Customer Details
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Company</Label>
                    {isEditing ? (
                      <Input
                        value={editedFields.customer_name}
                        onChange={(e) => setEditedFields({...editedFields, customer_name: e.target.value})}
                        className="mt-1"
                      />
                    ) : (
                      <p className="font-medium text-blue-600">{selectedEmail.customer_name || "-"}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Contact Person</Label>
                    {isEditing ? (
                      <Input
                        value={editedFields.contact_person}
                        onChange={(e) => setEditedFields({...editedFields, contact_person: e.target.value})}
                        className="mt-1"
                      />
                    ) : (
                      <p className="font-medium">{selectedEmail.contact_person || selectedEmail.sender_name || "-"}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Email</Label>
                    {isEditing ? (
                      <Input
                        value={editedFields.contact_email}
                        onChange={(e) => setEditedFields({...editedFields, contact_email: e.target.value})}
                        className="mt-1"
                      />
                    ) : (
                      <p className="font-medium">{selectedEmail.contact_email || selectedEmail.sender_email || "-"}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Service & Routing Section */}
              <div className="grid grid-cols-2 gap-4">
                {/* Service Details */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold text-sm flex items-center gap-2 mb-4">
                    <Ship className="h-4 w-4 text-slate-600" />
                    Service Details
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">Service Type</Label>
                      {isEditing ? (
                        <Select
                          value={editedFields.service_type}
                          onValueChange={(v) => setEditedFields({...editedFields, service_type: v})}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="FCL">FCL</SelectItem>
                            <SelectItem value="LCL">LCL</SelectItem>
                            <SelectItem value="Air">Air</SelectItem>
                            <SelectItem value="Breakbulk/RORO">Breakbulk/RORO</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="font-medium">{selectedEmail.service_type || "-"}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Incoterm</Label>
                      {isEditing ? (
                        <Select
                          value={editedFields.incoterm}
                          onValueChange={(v) => setEditedFields({...editedFields, incoterm: v})}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="EXW">EXW</SelectItem>
                            <SelectItem value="FOB">FOB</SelectItem>
                            <SelectItem value="CIF">CIF</SelectItem>
                            <SelectItem value="CFR">CFR</SelectItem>
                            <SelectItem value="DAP">DAP</SelectItem>
                            <SelectItem value="DDP">DDP</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="font-medium">{selectedEmail.incoterm || "-"}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Equipment</Label>
                      {isEditing ? (
                        <Input
                          value={editedFields.equipment}
                          onChange={(e) => setEditedFields({...editedFields, equipment: e.target.value})}
                          className="mt-1"
                          placeholder="e.g., 40'HC"
                        />
                      ) : (
                        <p className="font-medium">{selectedEmail.equipment || "-"}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Shipping Line</Label>
                      {isEditing ? (
                        <Input
                          value={editedFields.shipping_line}
                          onChange={(e) => setEditedFields({...editedFields, shipping_line: e.target.value})}
                          className="mt-1"
                        />
                      ) : (
                        <p className="font-medium">{selectedEmail.shipping_line || "-"}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Routing */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold text-sm flex items-center gap-2 mb-4 text-green-700">
                    <Globe className="h-4 w-4" />
                    Routing
                  </h4>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-green-600">Origin:</Label>
                        {isEditing ? (
                          <Input
                            value={editedFields.pol}
                            onChange={(e) => setEditedFields({...editedFields, pol: e.target.value})}
                            className="mt-1"
                            placeholder="Port of Loading"
                          />
                        ) : (
                          <p className="font-medium">{selectedEmail.pol ? `(${selectedEmail.pol})` : "(/)"}</p>
                        )}
                      </div>
                      <div>
                        <Label className="text-xs text-green-600">Destination:</Label>
                        {isEditing ? (
                          <Input
                            value={editedFields.pod}
                            onChange={(e) => setEditedFields({...editedFields, pod: e.target.value})}
                            className="mt-1"
                            placeholder="Port of Discharge"
                          />
                        ) : (
                          <p className="font-medium">{selectedEmail.pod ? `(${selectedEmail.pod})` : "(/)"}</p>
                        )}
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-green-600">Final Dest:</Label>
                      {isEditing ? (
                        <Input
                          value={editedFields.final_destination}
                          onChange={(e) => setEditedFields({...editedFields, final_destination: e.target.value})}
                          className="mt-1"
                          placeholder="Final delivery address"
                        />
                      ) : (
                        <p className="font-medium">{selectedEmail.final_destination || "-"}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Cargo Details */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h4 className="font-semibold text-sm flex items-center gap-2 mb-4 text-amber-700">
                  <Package className="h-4 w-4" />
                  Cargo Details
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-amber-600">Description</Label>
                    {isEditing ? (
                      <Input
                        value={editedFields.cargo_description}
                        onChange={(e) => setEditedFields({...editedFields, cargo_description: e.target.value})}
                        className="mt-1"
                      />
                    ) : (
                      <p className="font-medium">{selectedEmail.cargo_description || "-"}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-amber-600">HS Codes</Label>
                    {isEditing ? (
                      <Input
                        value={editedFields.hs_codes}
                        onChange={(e) => setEditedFields({...editedFields, hs_codes: e.target.value})}
                        className="mt-1"
                      />
                    ) : (
                      <p className="font-medium">{selectedEmail.hs_codes || "-"}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-amber-600">Weight</Label>
                    {isEditing ? (
                      <Input
                        value={editedFields.weight}
                        onChange={(e) => setEditedFields({...editedFields, weight: e.target.value})}
                        className="mt-1"
                        placeholder="e.g., 5000 KG"
                      />
                    ) : (
                      <p className="font-medium">{selectedEmail.weight || "-"}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-amber-600">Volume</Label>
                    {isEditing ? (
                      <Input
                        value={editedFields.volume}
                        onChange={(e) => setEditedFields({...editedFields, volume: e.target.value})}
                        className="mt-1"
                        placeholder="e.g., 25 CBM"
                      />
                    ) : (
                      <p className="font-medium">{selectedEmail.volume || "-"}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Pricing Breakdown */}
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold text-sm flex items-center gap-2 mb-4 text-green-700">
                  <DollarSign className="h-4 w-4" />
                  Pricing Breakdown
                </h4>
                <div className="space-y-3">
                  <div className="text-xs text-amber-600 font-medium">Raw Pricing Data</div>
                  <div className="grid grid-cols-1 gap-2 text-sm font-mono bg-slate-50 p-3 rounded">
                    <div className="flex justify-between border-b border-slate-200 pb-2">
                      <span className="font-medium">Trucking</span>
                      {isEditing ? (
                        <Input
                          value={editedFields.pricing_trucking}
                          onChange={(e) => setEditedFields({...editedFields, pricing_trucking: e.target.value})}
                          className="w-40 h-7 text-xs"
                          placeholder="Enter rate"
                        />
                      ) : (
                        <span>{selectedEmail.pricing_trucking || "-------------------"}</span>
                      )}
                    </div>
                    <div className="flex justify-between border-b border-slate-200 pb-2">
                      <span className="font-medium">Customs</span>
                      {isEditing ? (
                        <Input
                          value={editedFields.pricing_customs}
                          onChange={(e) => setEditedFields({...editedFields, pricing_customs: e.target.value})}
                          className="w-40 h-7 text-xs"
                          placeholder="Enter rate"
                        />
                      ) : (
                        <span>{selectedEmail.pricing_customs || "DDU/DAP (T1) Transit documentation\n-------------------"}</span>
                      )}
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium">Forwarding</span>
                      {isEditing ? (
                        <Input
                          value={editedFields.pricing_forwarding}
                          onChange={(e) => setEditedFields({...editedFields, pricing_forwarding: e.target.value})}
                          className="w-40 h-7 text-xs"
                          placeholder="Enter rate"
                        />
                      ) : (
                        <span>{selectedEmail.pricing_forwarding || "TCC    35,- eur/FCL"}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Original Email Body - Expandable */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-sm flex items-center gap-2">
                    <FileText className="h-4 w-4 text-slate-600" />
                    Original Email
                  </h4>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setShowFullBody(!showFullBody)}
                  >
                    <Maximize2 className="h-4 w-4 mr-2" />
                    {showFullBody ? "Collapse" : "Expand"}
                  </Button>
                </div>
                <div className={`bg-white border rounded-lg p-4 ${showFullBody ? 'min-h-[400px]' : 'max-h-[300px]'} overflow-y-auto`}>
                  <pre className="text-sm whitespace-pre-wrap font-sans leading-relaxed">
                    {selectedEmail.body_text || "No body text available"}
                  </pre>
                </div>
              </div>

              {/* Email Addresses Found */}
              {selectedEmail.all_email_addresses?.length > 0 && (
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-2">Email Addresses Found in this message:</p>
                  <div className="flex flex-wrap gap-1">
                    {selectedEmail.all_email_addresses.map((email, i) => (
                      <Badge key={i} variant="outline" className="text-xs">
                        {email}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
